import React, { useState, useRef } from 'react';

import { Paperclip, FolderLock, ArrowUp, FileText, Loader2 } from 'lucide-react';
import { HistoryPicker } from '../../components/chat/HistoryPicker';
import { AutocompleteOverlay } from '../../components/chat/AutocompleteOverlay';
import { listChatHistory } from '../../api/session.api';
import type { VaultFile } from '../../types/session.types';

export interface CommandInputAreaProps {
  onSendMessage: (text: string, historyRefs?: string[], vaultFileRefs?: string[]) => void;
  onAttachFile?: () => void;
  onAttachFiles?: (files: File[]) => void;
  onOpenVault?: () => void;
  onSaveSummary?: () => void;
  isSummarizing?: boolean;
  canSaveSummary?: boolean;
  disabled?: boolean;
  isVaultEnabled?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  vaultFiles?: VaultFile[];
}

// Module-level cache: shared across all CommandInputArea instances.
// This is intentional — the chat history list is user-scoped (not session-scoped),
// so all sessions see the same summaries. Cache invalidates after 5 minutes.
let historyCache: any[] | null = null;
let historyCacheTime: number = 0;

export const clearHistoryCache = () => {
  historyCache = null;
  historyCacheTime = 0;
};

export const CommandInputArea: React.FC<CommandInputAreaProps> = ({
  onSendMessage,
  onAttachFile,
  onAttachFiles,
  onOpenVault,
  onSaveSummary,
  isSummarizing = false,
  canSaveSummary = false,
  disabled = false,
  isVaultEnabled = true,
  value,
  onValueChange,
  placeholder,
  vaultFiles = [],
}) => {
  const isChatSummaryEnabled = import.meta.env.VITE_FEATURE_CHAT_SUMMARY === 'true';
  const [internalText, setInternalText] = useState('');
  const text = value !== undefined ? value : internalText;
  const setText = (newVal: string) => {
    if (onValueChange) onValueChange(newVal);
    else setInternalText(newVal);
  };

  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerIndex, setPickerIndex] = useState(0);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedVaultRefs, setSelectedVaultRefs] = useState<string[]>([]);
  const [showVaultPicker, setShowVaultPicker] = useState(false);
  const [vaultPickerQuery, setVaultPickerQuery] = useState('');
  const [vaultPickerIndex, setVaultPickerIndex] = useState(0);

  const selectVaultItem = (item: VaultFile) => {
    if (!selectedVaultRefs.includes(item.fileId)) {
      setSelectedVaultRefs((prev) => [...prev, item.fileId]);
    }
    const newVal = text.replace(/@[\w.\-\/]*$/, '');
    setText(newVal);
    setShowVaultPicker(false);
  };

  const filteredVault = React.useMemo(() => {
    if (!vaultFiles) return [];
    
    // Sort by lastModified descending to prioritize recent files
    const sorted = [...vaultFiles].sort(
      (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
    );

    if (!vaultPickerQuery) {
      return sorted.slice(0, 5);
    }

    return sorted.filter(
      (f) =>
        f.filename.toLowerCase().includes(vaultPickerQuery.toLowerCase()) ||
        f.fileId.toLowerCase().includes(vaultPickerQuery.toLowerCase())
    );
  }, [vaultFiles, vaultPickerQuery]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchHistoryList = () => {
    if (historyCache && Date.now() - historyCacheTime < 5 * 60 * 1000) {
      setHistoryList(historyCache);
      return;
    }
    setIsLoading(true);
    listChatHistory()
      .then((data) => {
        setHistoryList(data);
        historyCache = data;
        historyCacheTime = Date.now();
      })
      .catch((err) => {
        console.error('Failed to fetch chat history list', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const selectHistoryItem = (item: any) => {
    if (!selectedRefs.includes(item.displayName)) {
      setSelectedRefs((prev) => [...prev, item.displayName]);
    }
    const newVal = text.replace(/#[\w-]*$/, '');
    setText(newVal);
    setShowPicker(false);
  };

  const filteredHistory = historyList.filter((item) =>
    item.displayName.toLowerCase().includes(pickerQuery.toLowerCase()) ||
    item.agentName.toLowerCase().includes(pickerQuery.toLowerCase())
  );

  const handlePaperclipClick = () => {
    if (onAttachFiles && fileInputRef.current) {
      fileInputRef.current.click();
    } else if (onAttachFile) {
      onAttachFile();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleVaultClick = () => {
    let newText = text;
    if (!newText.endsWith('@')) {
      if (newText.length > 0 && !newText.endsWith(' ')) {
        newText += ' @';
      } else {
        newText += '@';
      }
    }
    setText(newText);
    setShowVaultPicker(true);
    setVaultPickerQuery('');
    setVaultPickerIndex(0);

    if (onOpenVault) {
      onOpenVault();
    }

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (onAttachFiles) {
        onAttachFiles(Array.from(e.target.files));
      }
      e.target.value = '';
    }
  };

  const handleSubmit = () => {
    if ((text.trim() || selectedRefs.length > 0 || selectedVaultRefs.length > 0) && !disabled) {
      onSendMessage(text.trim(), selectedRefs, selectedVaultRefs);
      setText('');
      setSelectedRefs([]);
      setSelectedVaultRefs([]);
      setShowPicker(false);
      setShowVaultPicker(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showPicker && filteredHistory.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPickerIndex((prev) => (prev + 1) % filteredHistory.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setPickerIndex((prev) => (prev - 1 + filteredHistory.length) % filteredHistory.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectHistoryItem(filteredHistory[pickerIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowPicker(false);
        return;
      }
    }

    if (showVaultPicker && filteredVault.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setVaultPickerIndex((prev) => (prev + 1) % filteredVault.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setVaultPickerIndex((prev) => (prev - 1 + filteredVault.length) % filteredVault.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectVaultItem(filteredVault[vaultPickerIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowVaultPicker(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);

    const match = val.match(/#([\w-]*)$/);
    const vaultMatch = val.match(/@([\w.\-\/]*)$/);

    if (match && isChatSummaryEnabled) {
      setShowPicker(true);
      setPickerQuery(match[1]);
      setPickerIndex(0);
      fetchHistoryList();
      setShowVaultPicker(false);
    } else if (vaultMatch) {
      setShowVaultPicker(true);
      setVaultPickerQuery(vaultMatch[1]);
      setVaultPickerIndex(0);
      setShowPicker(false);
    } else {
      setShowPicker(false);
      setShowVaultPicker(false);
    }

    // Auto-grow height logic
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const computed = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computed.lineHeight) || 20;
      const paddingTop = parseInt(computed.paddingTop) || 10;
      const paddingBottom = parseInt(computed.paddingBottom) || 10;
      const maxHeight = lineHeight * 4 + paddingTop + paddingBottom;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  };

  // Sync height if value changes externally
  React.useEffect(() => {
    if (textareaRef.current && value !== undefined) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const computed = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computed.lineHeight) || 20;
      const paddingTop = parseInt(computed.paddingTop) || 10;
      const paddingBottom = parseInt(computed.paddingBottom) || 10;
      const maxHeight = lineHeight * 4 + paddingTop + paddingBottom;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, [value]);

  return (
    <div className="absolute bottom-0 left-0 right-0 p-5 pt-3 bg-gradient-to-t from-surface via-surface/95 to-transparent">
      <div className="max-w-3xl mx-auto relative">
        {showPicker && (
          <HistoryPicker
            items={filteredHistory}
            activeIndex={pickerIndex}
            onSelect={selectHistoryItem}
            isLoading={isLoading}
          />
        )}

        {showVaultPicker && (
          <AutocompleteOverlay
            items={filteredVault}
            activeIndex={vaultPickerIndex}
            onSelect={selectVaultItem}
          />
        )}
        
        <div className={`bg-surface-container-low rounded-2xl shadow-inner px-2 py-2 flex flex-col gap-1.5 ring-1 ring-transparent focus-within:ring-primary/20 transition-all ${disabled ? 'opacity-60 bg-surface-container-high' : ''}`}>
          {(selectedRefs.length > 0 || selectedVaultRefs.length > 0) && (
            <div className="flex flex-wrap gap-1.5 px-3 py-1.5 border-b border-outline-variant/10">
              {/* History Chips */}
              {selectedRefs.map((ref) => (
                <div
                  key={ref}
                  className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-on-surface text-xs font-medium px-2 py-0.5 rounded-lg animate-fade-in"
                >
                  <span>@{ref}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedRefs((prev) => prev.filter((r) => r !== ref))}
                    className="text-secondary hover:text-on-surface transition-colors font-bold shrink-0 ml-0.5 animate-pulse"
                  >
                    ×
                  </button>
                </div>
              ))}
              {/* Vault Chips */}
              {selectedVaultRefs.map((ref) => {
                const filename = ref.split('/').pop() || ref;
                return (
                  <div
                    key={ref}
                    className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-900 text-xs font-medium px-2 py-0.5 rounded-lg animate-fade-in"
                  >
                    <span title={ref}>@{filename}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedVaultRefs((prev) => prev.filter((r) => r !== ref))}
                      className="text-blue-500 hover:text-blue-950 transition-colors font-bold shrink-0 ml-0.5"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="flex items-end gap-1 w-full">
            {isVaultEnabled && (
              <button
                type="button"
                disabled={disabled}
                onClick={handleVaultClick}
                className="p-2.5 text-secondary hover:bg-surface-container-low rounded-xl transition-colors shrink-0 disabled:cursor-not-allowed"
                title="Vault"
              >
                <FolderLock className="w-5 h-5" />
              </button>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: 'none' }}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={handlePaperclipClick}
              className="p-2.5 text-secondary hover:bg-surface-container-low rounded-xl transition-colors shrink-0 disabled:cursor-not-allowed"
              title="Attach file"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            {isChatSummaryEnabled && onSaveSummary && (
              <button
                type="button"
                disabled={disabled || isSummarizing}
                onClick={onSaveSummary}
                title={
                  isSummarizing
                    ? "Save in progress..."
                    : !canSaveSummary
                      ? "At least 2 turns required to save summary"
                      : "Save Chat Summary to Vault"
                }
                className={`p-2.5 rounded-xl transition-all shrink-0 disabled:cursor-not-allowed ${
                  isSummarizing
                    ? 'text-primary bg-primary/10 animate-pulse'
                    : !canSaveSummary
                      ? 'text-secondary/40 cursor-not-allowed'
                      : 'text-secondary hover:bg-surface-container-low hover:text-on-surface hover:scale-105 active:scale-95'
                }`}
              >
                {isSummarizing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
              </button>
            )}

            <textarea
              ref={textareaRef}
              rows={1}
              value={text}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={placeholder || (disabled ? "Session ended or turn limit reached" : "Ask a follow-up or upload more files…")}
              className="flex-1 bg-transparent border-none focus:ring-0 text-sm leading-5 py-2.5 px-1 resize-none outline-none disabled:cursor-not-allowed"
            />

            <button
              type="button"
              onClick={handleSubmit}
              disabled={(!text.trim() && selectedRefs.length === 0) || disabled}
              className={`p-2.5 rounded-xl shadow-md transition-all shrink-0 ${
                (text.trim() || selectedRefs.length > 0) && !disabled
                  ? 'bg-primary text-on-primary hover:scale-105 active:scale-95 cursor-pointer'
                  : 'bg-surface-container-high text-secondary/40 cursor-not-allowed'
              }`}
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
        <p className="text-[10px] text-center text-secondary/60 mt-2">
          Agent Capital utilises secure vault encryption for all financial artifacts. AI agents can make mistakes. <strong>Enter</strong> to send · <strong>Shift+Enter</strong> for new line.
        </p>
      </div>
    </div>
  );
};
